import React, { useEffect, useState } from 'react';
import {
  Form, Input, Select, DatePicker, Button, Card, InputNumber,
  message, Spin, Row, Col, Divider, Tag, Space, Modal, Checkbox, Descriptions, Flex
} from 'antd';
import Table from 'components/Common/Table';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import dayjs from 'dayjs';

const { TextArea } = Input;
const dateFormat = 'YYYY-MM-DD';

const REASON_OPTIONS = [
  { value: 'SALES_RETURN', label: 'Sales Return' },
  { value: 'POST_SALE_DISCOUNT', label: 'Post-Sale Discount' },
  { value: 'DEFICIENCY_IN_SERVICE', label: 'Deficiency in Service' },
  { value: 'CORRECTION', label: 'Correction in Invoice' },
  { value: 'CHANGE_IN_POS', label: 'Change in POS' },
  { value: 'GST_RATE_CHANGE', label: 'GST Rate Change' },
  { value: 'OTHER', label: 'Other' },
];

const CreditNoteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mainForm] = Form.useForm();
  const organization = useSelector((state) => state.auth.organization);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [lineItems, setLineItems] = useState([]);

  // Invoice item selection modal
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [selectedInvoiceItems, setSelectedInvoiceItems] = useState([]);
  const [showItemPicker, setShowItemPicker] = useState(false);

  // Apply credit modal
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyInvoiceId, setApplyInvoiceId] = useState(null);
  const [applyAmount, setApplyAmount] = useState(0);
  const [accountInvoices, setAccountInvoices] = useState([]);

  // Refund modal
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundPaymentMode, setRefundPaymentMode] = useState(null);
  const [refundTransactionRef, setRefundTransactionRef] = useState('');

  useEffect(() => {
    loadInvoices();
    loadTaxRates();
    if (id) {
      loadCreditNote();
    } else {
      generateCnNumber();
      // Pre-fill from invoice if invoice_id query param is present
      const invoiceId = searchParams.get('invoice_id');
      if (invoiceId) {
        mainForm.setFieldValue('invoice', invoiceId);
        onInvoiceChange(invoiceId);
      }
    }
  }, [id]);

  const loadCreditNote = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/creditnotes/credit-notes/${id}/`);
      if (response.status === 200) {
        const cn = response.data;
        setDataSource(cn);
        mainForm.setFieldsValue({
          ...cn,
          cn_date: cn.cn_date ? dayjs(cn.cn_date, 'YYYY-MM-DD') : null,
        });
        setLineItems(
          (cn.creditnoteitem_set || []).map((item, index) => ({ ...item, key: index }))
        );
        // Load account invoices for apply
        if (cn.account && cn.status !== 'DRAFT') {
          loadAccountInvoices(cn.account);
        }
      }
    } catch (err) {
      message.error('Failed to load credit note');
    }
    setLoading(false);
  };

  const loadInvoices = async () => {
    try {
      const response = await axios.get('/invoices/invoices/', {
        params: { page_size: 500, ordering: '-updated_at' }
      });
      if (response.status === 200) {
        setInvoices(response.data.results || []);
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
    }
  };

  const loadTaxRates = async () => {
    try {
      const response = await axios.get('/invoices/taxrates/');
      if (response.status === 200) {
        setTaxRates(response.data.results || response.data || []);
      }
    } catch (err) {
      console.error('Error loading tax rates:', err);
    }
  };

  const loadAccountInvoices = async (accountId) => {
    try {
      const response = await axios.get('/invoices/invoices/', {
        params: { account: accountId, page_size: 200 }
      });
      if (response.status === 200) {
        setAccountInvoices(response.data.results || []);
      }
    } catch (err) {
      console.error('Error loading account invoices:', err);
    }
  };

  const generateCnNumber = async () => {
    const orgId = process.env.REACT_APP_ORGANIZATION_UUID || organization;
    if (orgId) {
      try {
        const response = await axios.get('/creditnotes/credit-notes/generate_cn_number/', {
          params: { organization: orgId }
        });
        if (response.data?.cn_number) {
          mainForm.setFieldValue('cn_number', response.data.cn_number);
        }
      } catch (err) {
        console.error('Error generating CN number:', err);
      }
    }
  };

  const onInvoiceChange = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      const response = await axios.post('/creditnotes/credit-notes/create_from_invoice/', {
        invoice_id: invoiceId,
      });
      if (response.status === 200) {
        const { prefill, invoice_items } = response.data;
        mainForm.setFieldsValue({
          account: prefill.account,
          legal_name_of_business: prefill.legal_name_of_business,
          gst_number: prefill.gst_number,
          place_of_supply: prefill.place_of_supply,
          billing_address1: prefill.billing_address1,
          billing_address2: prefill.billing_address2,
          billing_city: prefill.billing_city,
          billing_state: prefill.billing_state,
          billing_postal_code: prefill.billing_postal_code,
          billing_country: prefill.billing_country,
          billing_phone: prefill.billing_phone,
          currency: prefill.currency,
          tax_rate1: prefill.tax_rate1,
          tax_rate2: prefill.tax_rate2,
          tax_rate3: prefill.tax_rate3,
        });
        setInvoiceItems(invoice_items || []);
      }
    } catch (err) {
      message.error('Failed to load invoice data');
    }
  };

  const handlePickItems = () => {
    setSelectedInvoiceItems([]);
    setShowItemPicker(true);
  };

  const handleConfirmItemPick = () => {
    const picked = invoiceItems.filter((_, i) => selectedInvoiceItems.includes(i));
    const newItems = picked.map((item, index) => ({
      key: lineItems.length + index,
      original_invoice_item: item.id,
      item_type: item.item_type,
      title: item.title,
      description: item.description,
      price: item.price,
      quantity: item.quantity,
      unit: item.unit,
      hsn: item.hsn,
      line_total: item.line_total,
      tax_rate1: item.tax_rate1,
      tax_rate2: item.tax_rate2,
      tax_rate3: item.tax_rate3,
      priority: lineItems.length + index,
    }));
    setLineItems([...lineItems, ...newItems]);
    setShowItemPicker(false);
  };

  const removeLineItem = (key) => {
    setLineItems(lineItems.filter(item => item.key !== key));
  };

  const updateLineItem = (key, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.key !== key) return item;
      const updated = { ...item, [field]: value };
      if (field === 'price' || field === 'quantity') {
        updated.line_total = (updated.price || 0) * (updated.quantity || 0);
      }
      return updated;
    }));
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.line_total || 0), 0);

  const getTaxAmount = (taxRateId) => {
    if (!taxRateId) return 0;
    const rate = taxRates.find(t => t.id === taxRateId);
    return rate ? subtotal * (rate.rate / 100) : 0;
  };

  const tax1 = getTaxAmount(mainForm.getFieldValue('tax_rate1'));
  const tax2 = getTaxAmount(mainForm.getFieldValue('tax_rate2'));
  const tax3 = getTaxAmount(mainForm.getFieldValue('tax_rate3'));
  const totalAmount = Math.round(subtotal + tax1 + tax2 + tax3);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const orgId = process.env.REACT_APP_ORGANIZATION_UUID || organization;
      const payload = {
        ...values,
        organization: orgId,
        cn_date: values.cn_date?.format(dateFormat),
        creditnoteitem_set: lineItems.map(item => {
          const cleaned = { ...item };
          if (typeof cleaned.key === 'number' || typeof cleaned.id === 'number') {
            delete cleaned.id;
          }
          delete cleaned.key;
          return cleaned;
        }),
        net: subtotal,
      };

      if (id) {
        const response = await axios.put(`/creditnotes/credit-notes/${id}/`, payload);
        if (response.status === 200) {
          message.success('Credit note updated');
          loadCreditNote();
        }
      } else {
        const response = await axios.post('/creditnotes/credit-notes/', payload);
        if (response.status === 201) {
          message.success('Credit note created');
          navigate(`/credit-notes/edit/${response.data.id}`);
        }
      }
    } catch (err) {
      message.error('Failed to save credit note');
      console.error(err);
    }
    setSubmitting(false);
  };

  const handleApprove = async () => {
    try {
      const response = await axios.post(`/creditnotes/credit-notes/${id}/approve/`);
      if (response.status === 200) {
        message.success('Credit note approved');
        loadCreditNote();
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleApplyCredit = async () => {
    if (!applyInvoiceId || !applyAmount) {
      message.warning('Select invoice and enter amount');
      return;
    }
    try {
      const response = await axios.post(`/creditnotes/credit-notes/${id}/apply/`, {
        invoice_id: applyInvoiceId,
        amount: applyAmount,
      });
      if (response.status === 200) {
        message.success('Credit applied');
        setShowApplyModal(false);
        loadCreditNote();
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to apply credit');
    }
  };

  const handleRefund = async () => {
    if (!refundAmount) {
      message.warning('Enter refund amount');
      return;
    }
    try {
      const response = await axios.post(`/creditnotes/credit-notes/${id}/refund/`, {
        amount: refundAmount,
        payment_mode: refundPaymentMode,
        transaction_ref: refundTransactionRef,
      });
      if (response.status === 200) {
        message.success('Refund issued');
        setShowRefundModal(false);
        loadCreditNote();
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to issue refund');
    }
  };

  const itemColumns = [
    { title: 'Title', dataIndex: 'title', key: 'title',
      render: (val, record) => (
        <Input value={val} onChange={e => updateLineItem(record.key, 'title', e.target.value)} />
      ),
    },
    { title: 'HSN', dataIndex: 'hsn', key: 'hsn', width: 100,
      render: (val, record) => (
        <Input value={val} onChange={e => updateLineItem(record.key, 'hsn', e.target.value)} />
      ),
    },
    { title: 'Price', dataIndex: 'price', key: 'price', width: 120,
      render: (val, record) => (
        <InputNumber value={val} onChange={v => updateLineItem(record.key, 'price', v)} style={{ width: '100%' }} />
      ),
    },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 80,
      render: (val, record) => (
        <InputNumber value={val} onChange={v => updateLineItem(record.key, 'quantity', v)} style={{ width: '100%' }} />
      ),
    },
    { title: 'Line Total', dataIndex: 'line_total', key: 'line_total', width: 120,
      render: (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`,
    },
    { title: '', key: 'action', width: 50,
      render: (_, record) => (
        <Button type="text" danger onClick={() => removeLineItem(record.key)}>X</Button>
      ),
    },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const isEditable = !dataSource || dataSource.status === 'DRAFT';
  const canApply = dataSource && ['APPROVED', 'PARTIALLY_APPLIED'].includes(dataSource.status);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>{id ? `Credit Note: ${dataSource?.cn_number || ''}` : 'New Credit Note'}</h2>
        <Space>
          {dataSource?.status && (
            <Tag color={
              dataSource.status === 'DRAFT' ? 'default' :
              dataSource.status === 'APPROVED' ? 'blue' :
              dataSource.status === 'APPLIED' ? 'green' :
              dataSource.status === 'PARTIALLY_APPLIED' ? 'orange' : 'red'
            } style={{ fontSize: 14, padding: '4px 12px' }}>
              {dataSource.status.replace('_', ' ')}
            </Tag>
          )}
          {id && dataSource?.status === 'DRAFT' && (
            <Button type="primary" onClick={handleApprove}>Approve</Button>
          )}
          {canApply && (
            <>
              <Button onClick={() => { setApplyAmount(0); setApplyInvoiceId(null); setShowApplyModal(true); }}>
                Apply to Invoice
              </Button>
              <Button onClick={() => { setRefundAmount(0); setShowRefundModal(true); }}>
                Issue Refund
              </Button>
            </>
          )}
        </Space>
      </div>

      <Form form={mainForm} layout="vertical" onFinish={onFinish} disabled={!isEditable}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="cn_number" label="CN Number">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="invoice" label="Invoice" rules={[{ required: true, message: 'Select invoice' }]}>
              <Select
                showSearch
                placeholder="Select invoice"
                optionFilterProp="children"
                onChange={onInvoiceChange}
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {invoices.map(inv => (
                  <Select.Option key={inv.id} value={inv.id}>
                    {inv.inv_number} - {inv.account_data?.name || 'N/A'}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="cn_date" label="Credit Note Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
              <Select options={REASON_OPTIONS} placeholder="Select reason" />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="reason_notes" label="Reason Notes">
              <TextArea rows={1} />
            </Form.Item>
          </Col>
        </Row>

        {/* Customer Info (readonly, populated from invoice) */}
        <Card title="Customer Information" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="legal_name_of_business" label="Business Name">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="gst_number" label="GST Number">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="billing_city" label="City">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Line Items */}
        <Card
          title="Line Items"
          size="small"
          style={{ marginBottom: 16 }}
          extra={
            isEditable && invoiceItems.length > 0 && (
              <Button type="dashed" onClick={handlePickItems}>
                Pick from Invoice Items
              </Button>
            )
          }
        >
          <Table
            columns={itemColumns}
            dataSource={lineItems}
            rowKey="key"
            pagination={false}
            size="small"
          />
          {isEditable && (
            <Button
              type="dashed"
              style={{ width: '100%', marginTop: 8 }}
              onClick={() => {
                setLineItems([...lineItems, {
                  key: lineItems.length,
                  title: '', description: '', price: 0, quantity: 1,
                  line_total: 0, hsn: '', unit: 'FIXED', priority: lineItems.length,
                }]);
              }}
            >
              + Add Line Item
            </Button>
          )}
        </Card>

        {/* Tax & Totals */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="tax_rate1" label="Tax Rate 1">
                <Select allowClear placeholder="Select tax rate">
                  {taxRates.map(t => (
                    <Select.Option key={t.id} value={t.id}>{t.title} ({t.rate}%)</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tax_rate2" label="Tax Rate 2">
                <Select allowClear placeholder="Select tax rate">
                  {taxRates.map(t => (
                    <Select.Option key={t.id} value={t.id}>{t.title} ({t.rate}%)</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tax_rate3" label="Tax Rate 3">
                <Select allowClear placeholder="Select tax rate">
                  {taxRates.map(t => (
                    <Select.Option key={t.id} value={t.id}>{t.title} ({t.rate}%)</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ margin: '8px 0' }} />
          <Row justify="end">
            <Col span={8}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Subtotal">₹{subtotal.toLocaleString('en-IN')}</Descriptions.Item>
                {tax1 > 0 && <Descriptions.Item label="Tax 1">₹{tax1.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Descriptions.Item>}
                {tax2 > 0 && <Descriptions.Item label="Tax 2">₹{tax2.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Descriptions.Item>}
                {tax3 > 0 && <Descriptions.Item label="Tax 3">₹{tax3.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Descriptions.Item>}
                <Descriptions.Item label={<strong>Total</strong>}><strong>₹{totalAmount.toLocaleString('en-IN')}</strong></Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>

        {/* Notes */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="public_notes" label="Public Notes">
              <TextArea rows={3} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="private_notes" label="Private Notes">
              <TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>

        {/* Hidden fields */}
        <Form.Item name="account" hidden><Input /></Form.Item>
        <Form.Item name="place_of_supply" hidden><Input /></Form.Item>
        <Form.Item name="billing_address1" hidden><Input /></Form.Item>
        <Form.Item name="billing_address2" hidden><Input /></Form.Item>
        <Form.Item name="billing_state" hidden><Input /></Form.Item>
        <Form.Item name="billing_postal_code" hidden><Input /></Form.Item>
        <Form.Item name="billing_country" hidden><Input /></Form.Item>
        <Form.Item name="billing_phone" hidden><Input /></Form.Item>
        <Form.Item name="currency" hidden><Input /></Form.Item>

        {isEditable && (
          <Form.Item style={{ marginTop: 16 }}>
            <Flex justify="end" gap="middle">
              <Button type="primary" htmlType="submit" loading={submitting} size="large">
                {id ? 'Update Credit Note' : 'Create Credit Note'}
              </Button>
            </Flex>
          </Form.Item>
        )}
      </Form>

      {/* Allocations section (visible when not DRAFT) */}
      {dataSource && dataSource.status !== 'DRAFT' && (
        <Card title="Credit Allocations" size="small" style={{ marginTop: 16 }}>
          {dataSource.unapplied_amount > 0 && (
            <p style={{ marginBottom: 8 }}>
              <strong>Unapplied Balance:</strong> ₹{Number(dataSource.unapplied_amount).toLocaleString('en-IN')}
            </p>
          )}
          <Table
            columns={[
              { title: 'Date', dataIndex: 'allocation_date', render: d => d ? dayjs(d).format('DD MMM YYYY') : '-' },
              { title: 'Invoice', dataIndex: 'invoice', render: (invId) => {
                if (!invId) return 'Refund';
                const inv = accountInvoices.find(i => i.id === invId);
                return inv ? inv.inv_number : invId;
              }},
              { title: 'Amount', dataIndex: 'amount', render: v => `₹${Number(v).toLocaleString('en-IN')}`, align: 'right' },
              { title: 'Notes', dataIndex: 'notes' },
            ]}
            dataSource={dataSource.creditnoteallocation_set || []}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Invoice Item Picker Modal */}
      <Modal
        title="Select Invoice Items"
        open={showItemPicker}
        onOk={handleConfirmItemPick}
        onCancel={() => setShowItemPicker(false)}
        width={700}
      >
        <Checkbox.Group
          value={selectedInvoiceItems}
          onChange={setSelectedInvoiceItems}
          style={{ width: '100%' }}
        >
          <Table
            columns={[
              { title: '', key: 'select', width: 40,
                render: (_, __, index) => <Checkbox value={index} />,
              },
              { title: 'Title', dataIndex: 'title' },
              { title: 'Price', dataIndex: 'price', render: v => `₹${v}` },
              { title: 'Qty', dataIndex: 'quantity' },
              { title: 'Total', dataIndex: 'line_total', render: v => `₹${v}` },
            ]}
            dataSource={invoiceItems}
            rowKey={(_, index) => index}
            pagination={false}
            size="small"
          />
        </Checkbox.Group>
      </Modal>

      {/* Apply Credit Modal */}
      <Modal
        title="Apply Credit to Invoice"
        open={showApplyModal}
        onOk={handleApplyCredit}
        onCancel={() => setShowApplyModal(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Select Invoice">
            <Select
              showSearch
              placeholder="Select invoice"
              optionFilterProp="children"
              value={applyInvoiceId}
              onChange={setApplyInvoiceId}
            >
              {accountInvoices
                .filter(inv => inv.balance > 0)
                .map(inv => (
                  <Select.Option key={inv.id} value={inv.id}>
                    {inv.inv_number} (Balance: ₹{Number(inv.balance).toLocaleString('en-IN')})
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item label="Amount">
            <InputNumber
              value={applyAmount}
              onChange={setApplyAmount}
              style={{ width: '100%' }}
              min={0}
              max={dataSource?.unapplied_amount || 0}
              prefix="₹"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Refund Modal */}
      <Modal
        title="Issue Refund"
        open={showRefundModal}
        onOk={handleRefund}
        onCancel={() => setShowRefundModal(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Refund Amount">
            <InputNumber
              value={refundAmount}
              onChange={setRefundAmount}
              style={{ width: '100%' }}
              min={0}
              max={dataSource?.unapplied_amount || 0}
              prefix="₹"
            />
          </Form.Item>
          <Form.Item label="Payment Mode">
            <Select value={refundPaymentMode} onChange={setRefundPaymentMode} allowClear>
              {['UPI', 'NEFT', 'RTGS', 'IMPS', 'CASH', 'BANK_TRANSFER'].map(m => (
                <Select.Option key={m} value={m}>{m}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Transaction Reference">
            <Input value={refundTransactionRef} onChange={e => setRefundTransactionRef(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CreditNoteForm;
